import { Button, Tooltip } from "antd";
import {
  SaveOutlined,
  FolderOpenOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { useRef } from "react";
import type React from "react";
import dayjs from "dayjs";
import type { Filter } from "../../../context/VictimsAndKillersContext";
import { useVictimsAndKillers } from "../../../hooks/useVictimsAndKillers";
import { useCustomMarkers } from "../../../hooks/useCustomMarkers";
import { useHighlight } from "../../../hooks/useHighlight";
import type {
  CustomMarkerGroup,
  CustomMarker,
} from "../../../context/CustomMarkersContext";
import { useLoading } from "../../../hooks/useLoading";
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useNotesAndPictures } from "../../../hooks/useNotesAndPictures";
import type { PersistedOverlayItem } from "../../../context/NotesAndPicturesContext";
import styled from "styled-components";

const TitleActions = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
  padding-right: 32px;
`;

const ActionButton = styled(Button)`
  color: #eeeeee !important;
  background: transparent !important;
  border: none !important;
  font-size: 20px;
  &:hover {
    color: #8e1616 !important;
    background: #393e46 !important;
  }
`;

type PersistedMarker = Omit<CustomMarker, "date"> & {
  date: string | null;
  highlighted?: boolean | null;
};
type PersistedGroup = Omit<CustomMarkerGroup, "markers"> & {
  markers: PersistedMarker[];
};

function serializeGroups(
  groups: CustomMarkerGroup[],
  highlightedIds: Set<string>,
): PersistedGroup[] {
  return groups.map((g) => ({
    ...g,
    markers: g.markers.map((m) => ({
      ...m,
      date: m.date ? m.date.toISOString() : null,
      highlighted: highlightedIds.has(m.id) ? true : null,
    })),
  }));
}

function deserializeGroups(raw: unknown): CustomMarkerGroup[] {
  if (!Array.isArray(raw)) return [];
  return (raw as any[]).flatMap((g) => {
    if (!g || typeof g !== "object") return [];
    return [
      {
        id: Number(g.id) || 0,
        name: typeof g.name === "string" ? g.name : "Marker group",
        color: typeof g.color === "string" ? g.color : "#1677ff",
        markers: Array.isArray(g.markers)
          ? (g.markers as any[]).flatMap((m) => {
              if (!m || typeof m !== "object") return [];
              return [
                {
                  id:
                    typeof m.id === "string"
                      ? m.id
                      : String(m.id ?? crypto.randomUUID()),
                  name: typeof m.name === "string" ? m.name : "",
                  date:
                    typeof m.date === "string" && m.date ? dayjs(m.date) : null,
                  latitude: typeof m.latitude === "string" ? m.latitude : "",
                  longitude: typeof m.longitude === "string" ? m.longitude : "",
                  description:
                    typeof m.description === "string" ? m.description : "",
                },
              ];
            })
          : [],
      },
    ];
  });
}

export default function DrawerTitleActions() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { filters, clearFilters, importFilters } = useVictimsAndKillers();
  const { groups, importGroups, clearGroups } = useCustomMarkers();
  const {
    highlightedIds,
    highlightColor,
    showOnlyHighlighted,
    importHighlights,
    clearHighlights,
  } = useHighlight();
  const { show: showLoading, hide: hideLoading } = useLoading();
  const { confirm } = useConfirmModal();
  const { items: noteItems, importItems, clearItems } = useNotesAndPictures();

  const handleSave = async () => {
    const customMarkerIds = new Set(
      groups.flatMap((g) => g.markers.map((m) => m.id)),
    );
    const victimHighlights = Array.from(highlightedIds)
      .filter((id) => !customMarkerIds.has(id))
      .map((id) => ({ id, highlighted: true as true | null }));
    const payload = {
      crimes: filters,
      markers: serializeGroups(groups, highlightedIds),
      highlight: {
        color: highlightColor,
        showOnlyHighlighted,
        victimHighlights,
      },
      notesAndPictures: noteItems,
    };
    const json = JSON.stringify(payload, null, 2);
    if ("showSaveFilePicker" in window) {
      try {
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: "crime-stalkers-save.json",
          types: [
            {
              description: "JSON file",
              accept: { "application/json": [".json"] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(json);
        await writable.close();
        return;
      } catch (err: any) {
        if (err?.name === "AbortError") return;
      }
    }
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "crime-stalkers-save.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    showLoading();
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const raw = JSON.parse(ev.target?.result as string);
        const parsedCrimes: Filter[] = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.crimes)
            ? raw.crimes
            : [];
        if (parsedCrimes.length) importFilters(parsedCrimes);
        const parsedMarkers = deserializeGroups(raw?.markers);
        if (parsedMarkers.length) importGroups(parsedMarkers);
        // Collect highlighted IDs from custom markers
        const markerHighlightedIds: string[] = [];
        if (Array.isArray(raw?.markers)) {
          for (const g of raw.markers as any[]) {
            if (!Array.isArray(g?.markers)) continue;
            for (const m of g.markers as any[]) {
              if (m?.highlighted === true && typeof m.id === "string")
                markerHighlightedIds.push(m.id);
            }
          }
        }
        // Collect highlighted IDs from victimHighlights array
        const victimHighlightedIds: string[] = [];
        if (Array.isArray(raw?.highlight?.victimHighlights)) {
          for (const entry of raw.highlight.victimHighlights as any[]) {
            if (entry?.highlighted === true && typeof entry.id === "string")
              victimHighlightedIds.push(entry.id);
          }
        }
        const allHighlightedIds = [
          ...markerHighlightedIds,
          ...victimHighlightedIds,
        ];
        if (allHighlightedIds.length > 0 || raw?.highlight) {
          const color =
            typeof raw?.highlight?.color === "string"
              ? raw.highlight.color
              : "#FFD700";
          const showOnly = raw?.highlight?.showOnlyHighlighted === true;
          importHighlights(allHighlightedIds, color, showOnly);
        }
        if (Array.isArray(raw?.notesAndPictures)) {
          const parsedNotes: PersistedOverlayItem[] = (
            raw.notesAndPictures as any[]
          )
            .filter(
              (n) =>
                n &&
                typeof n.id === "string" &&
                (n.kind === "note" || n.kind === "picture"),
            )
            .map((n) => ({
              id: n.id,
              kind: n.kind,
              size: ["S", "M", "L"].includes(n.size)
                ? n.size
                : n.kind === "note"
                  ? "S"
                  : "M",
              position:
                n.position &&
                typeof n.position.x === "number" &&
                typeof n.position.y === "number"
                  ? n.position
                  : { x: 200, y: 200 },
              noteText: typeof n.noteText === "string" ? n.noteText : "",
              pictureUrl:
                typeof n.pictureUrl === "string" ? n.pictureUrl : null,
              collapsed: n.collapsed === true,
            }));
          if (parsedNotes.length) importItems(parsedNotes);
        }
      } finally {
        hideLoading();
      }
    };
    reader.onerror = () => hideLoading();
    reader.readAsText(file);
    e.target.value = "";
  };

  return (
    <TitleActions>
      <Tooltip title="Save map data">
        <ActionButton
          type="text"
          icon={<SaveOutlined />}
          onClick={handleSave}
          aria-label="Save"
        />
      </Tooltip>
      <Tooltip title="Import map data">
        <ActionButton
          type="text"
          icon={<FolderOpenOutlined />}
          onClick={() => fileInputRef.current?.click()}
          aria-label="Import"
        />
      </Tooltip>
      <Tooltip title="Clear map">
        <ActionButton
          type="text"
          icon={<ClearOutlined />}
          onClick={() =>
            confirm({
              question: "Are you sure you want to clear the map?",
              onOk: () => {
                clearFilters();
                clearGroups();
                clearHighlights();
                clearItems();
              },
            })
          }
          aria-label="Clear"
        />
      </Tooltip>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        style={{ display: "none" }}
        onChange={handleImport}
      />
    </TitleActions>
  );
}
